import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { clearLocalSession, redirectToUnifiedAuth } from '../utils/unifiedAuth';

const AuthContext = createContext(null);

const readCachedUser = () => {
  try {
    const value = localStorage.getItem('cached_user');
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readCachedUser);
  const [loading, setLoading] = useState(() => !readCachedUser());

  const fetchUser = async (retryCount = 0) => {
    const maxRetries = 1;
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.warn('Токен отсутствует, пропускаем запрос пользователя');
      setLoading(false);
      setUser(null);
      return;
    }

    try {
      console.log('AuthContext: Запрашиваем данные пользователя');
      const response = await api.get('/core/users/me/', { timeout: 12_000, _skipNetworkRetry: true });
      console.log('AuthContext: Данные пользователя получены:', response.data);
      setUser(response.data);
      localStorage.setItem('cached_user', JSON.stringify(response.data));
      // Сохраняем проекты пользователя в localStorage (опционально)
      localStorage.setItem('user_projects', JSON.stringify(response.data.projects || []));
      localStorage.setItem('user_full_name', response.data.full_name || response.data.username || '');
      localStorage.setItem('user_profile_photo', response.data.profile_photo || '');
    } catch (error) {
      console.error('AuthContext: Ошибка получения данных пользователя:', error);
      const status = error.response?.status;
      const transientError = !status || status >= 500 || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
      if (transientError && retryCount < maxRetries) {
        const delay = 700 * (retryCount + 1);
        console.warn(`AuthContext: Временная ошибка сети, повтор ${retryCount + 1} из ${maxRetries}`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchUser(retryCount + 1);
      }

      if (status === 401 || status === 403) {
        console.error('AuthContext: Авторизация недействительна');
        setUser(null);
        clearLocalSession();
        return;
      }

      // Сетевой сбой не должен разлогинивать пользователя и стирать выбранный проект.
      const cachedUser = readCachedUser();
      if (cachedUser) {
        setUser(cachedUser);
      }
      console.warn('AuthContext: Используются сохранённые данные пользователя до восстановления сети');
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    setLoading(true);
    await fetchUser();
  };

  const logout = () => {
    console.log('AuthContext: Выполняется выход пользователя');
    setUser(null);
    clearLocalSession();
    redirectToUnifiedAuth('/');
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
